import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Modal, ModalBody, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { RefreshCw, X } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { getTelrSessions } from "../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

// Every Telr payment ATTEMPT, not just the ones that produced money.
//
// The Payments ledger can only ever show captured payments. This page is the other
// half: declined cards, sheets the customer backed out of, attempts that expired, and
// anything still pending. When a customer says "I tried three times and it failed",
// this is the only place that shows it.

const STATUS_COLOR = {
  paid: { bg: C.greenLight, fg: C.green },
  pending: { bg: C.orangeLight, fg: C.orange },
  declined: { bg: C.coralLight, fg: C.coral },
  cancelled: { bg: C.bg, fg: C.textMed },
  expired: { bg: C.bg, fg: C.textMed },
};

// What each status actually means, in the words an admin needs. These are not obvious
// from the word alone — "expired" in particular is routine, not a fault.
const STATUS_HELP = {
  paid: "Money captured by Telr.",
  pending: "Started but no outcome yet. The 5-minute sweep settles these.",
  declined: "The bank refused the card.",
  cancelled: "The customer closed the payment sheet without paying.",
  expired: "Never completed; Telr expired it. Normal for an abandoned attempt.",
};

const METHOD_LABEL = {
  card: "Card",
  applepay: "Apple Pay",
  saved_card: "Saved card",
  hosted: "Web (hosted page)",
  applepay_web: "Apple Pay (web)",
};

const BRAND_LABEL = {
  visa: "Visa",
  mastercard: "Mastercard",
  mada: "mada",
  amex: "Amex",
  maestro: "Maestro",
};

// "Visa ••1111" — the server reads brand/last4 off Telr's record; nothing here guesses.
const cardLabel = (row) => {
  if (!row?.cardBrand && !row?.cardLast4) return null;
  const brand = row.cardBrand ? BRAND_LABEL[row.cardBrand] || row.cardBrand : "Card";
  return row.cardLast4 ? `${brand} ••${row.cardLast4}` : brand;
};

const Pill = ({ value, palette }) => {
  const c = palette[value] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "capitalize",
        background: c.bg,
        color: c.fg,
      }}
    >
      {value || "—"}
    </span>
  );
};

const filterSelectStyle = {
  padding: "7px 12px",
  fontSize: 12,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  cursor: "pointer",
  minWidth: 140,
};

const TelrSessions = () => {
  const selectedMerchant = useSelector((state) => state.merchant?.selectedMerchant);

  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null);

  const [filters, setFilters] = useState({
    MerchantId: selectedMerchant || "",
    status: "",
    method: "",
    search: "",
    userId: "",
    limit: 20,
  });

  const fetchSessions = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      const data = await getTelrSessions(page, filters.limit, filters);
      if (data) {
        setSessions(data.rows || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
        setSummary(data.summary || {});
      } else {
        setSessions([]);
        setTotalPages(1);
        setTotal(0);
        setSummary({});
      }
      setIsLoading(false);
    },
    [filters]
  );

  useEffect(() => {
    fetchSessions(currentPage);
  }, [fetchSessions, currentPage]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, MerchantId: selectedMerchant || "" }));
    setCurrentPage(1);
  }, [selectedMerchant]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const customerName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.phoneNo || "—";

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.min(totalPages, currentPage + 2)
  );

  // Clicking a chip filters to that status; clicking the active one clears it.
  const toggleStatus = (status) => {
    setFilters((prev) => ({ ...prev, status: prev.status === status ? "" : status }));
    setCurrentPage(1);
  };

  return (
    <Fragment>
      <Breadcrumb title="Telr Sessions" parent="Orders" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <div
                onClick={() => fetchSessions(currentPage)}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: C.white,
                  flexShrink: 0,
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <input
                placeholder="Search Telr ref / cart id / external id"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    fetchSessions(1);
                  }
                }}
                style={{ ...filterSelectStyle, minWidth: 260, cursor: "text" }}
              />

              <input
                type="text"
                inputMode="numeric"
                placeholder="User ID"
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value.replace(/\D/g, "") })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    fetchSessions(1);
                  }
                }}
                style={{ ...filterSelectStyle, minWidth: 110, cursor: "text" }}
              />

              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="declined">Declined</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>

              <select
                value={filters.method}
                onChange={(e) => {
                  setFilters({ ...filters, method: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All methods</option>
                <option value="card">Card</option>
                <option value="applepay">Apple Pay</option>
                <option value="saved_card">Saved card</option>
                <option value="hosted">Web (hosted page)</option>
                <option value="applepay_web">Apple Pay (web)</option>
              </select>

              <span style={{ fontSize: 12, color: C.textMed, marginLeft: "auto" }}>
                {total} attempt{total === 1 ? "" : "s"}
              </span>
            </div>

            {/* Status breakdown. Counts ignore the status filter on purpose, so they stay a
                stable picture while you drill into one of them. A pending count that keeps
                climbing is the signal that the reconcile sweep is not clearing attempts. */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {["paid", "pending", "declined", "cancelled", "expired"].map((s) => (
                <div
                  key={s}
                  onClick={() => toggleStatus(s)}
                  title={STATUS_HELP[s]}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 10,
                    border: `1.5px solid ${filters.status === s ? C.coral : C.border}`,
                    background: filters.status === s ? C.coralLight : C.white,
                    cursor: "pointer",
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ textTransform: "capitalize", color: C.textMed, fontWeight: 600 }}>{s}</span>
                  <span style={{ fontWeight: 800, color: C.text }}>{summary[s] ?? 0}</span>
                </div>
              ))}
            </div>

            {isLoading ? (
              <RLoader />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Started</th>
                      <th style={tbl.th}>Status</th>
                      <th style={tbl.th}>Amount</th>
                      <th style={tbl.th}>Method</th>
                      <th style={tbl.th}>Customer</th>
                      <th style={tbl.th}>Cart (external id)</th>
                      <th style={tbl.th}>Order</th>
                      <th style={tbl.th}>Branch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions?.length > 0 ? (
                      sessions.map((s) => (
                        <tr
                          key={s.id}
                          onClick={() => setDetail(s)}
                          style={{ cursor: "pointer" }}
                          className="rpt-row"
                        >
                          <td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>#{s.id}</td>
                          <td style={tbl.td}>{formatDate(s.createdAt)}</td>
                          <td style={tbl.td}>
                            <span title={STATUS_HELP[s.status]}>
                              <Pill value={s.status} palette={STATUS_COLOR} />
                            </span>
                          </td>
                          <td style={tbl.td}>
                            {s.amount ?? "—"} {s.currency ?? ""}
                          </td>
                          <td style={tbl.td}>
                            {METHOD_LABEL[s.method] || s.method}
                            {cardLabel(s) && (
                              <div style={{ fontSize: 11, color: C.textMed }}>{cardLabel(s)}</div>
                            )}
                          </td>
                          <td style={tbl.td}>{customerName(s.User)}</td>
                          <td
                            style={{ ...tbl.td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={s.externalId}
                          >
                            {s.externalId}
                          </td>
                          <td style={tbl.td}>
                            {s.order ? (
                              <span
                                style={{ fontWeight: 700, color: C.text }}
                                title={
                                  s.status === "paid"
                                    ? `Order ${s.order.id} (${s.order.status})`
                                    : `This attempt failed, but the cart was eventually paid by another attempt — order ${s.order.id}`
                                }
                              >
                                #{s.order.id}
                                {s.status !== "paid" ? "*" : ""}
                              </span>
                            ) : (
                              <span style={{ color: C.textLight }}>—</span>
                            )}
                          </td>
                          <td style={tbl.td}>{s.Branch?.name ?? "—"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="9"
                          style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}
                        >
                          No Telr payment attempts found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {sessions?.length > 0 && (
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 10 }}>
                    * the cart has an order, but it was paid by a different attempt than this row.
                  </div>
                )}
              </div>
            )}

            {sessions?.length > 0 && totalPages > 1 && (
              <Pagination className="d-flex justify-content-end mt-3 mb-0">
                <PaginationItem disabled={currentPage <= 1}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                </PaginationItem>
                {pageNumbers.map((i) => (
                  <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage >= totalPages}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            )}
          </RCard>
        </div>

        <SessionDetailModal session={detail} onClose={() => setDetail(null)} customerName={customerName} />
      </Container>
    </Fragment>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${C.borderLight}` }}>
    <div style={{ flex: "0 0 150px", fontSize: 12, fontWeight: 600, color: C.textMed }}>{label}</div>
    <div
      style={{
        flex: 1,
        fontSize: 13,
        color: C.text,
        wordBreak: "break-word",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </div>
);

const JsonBlock = ({ data }) => (
  <pre
    style={{
      margin: 0,
      padding: 12,
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      fontSize: 11,
      lineHeight: 1.5,
      color: C.text,
      maxHeight: 260,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {(() => {
      try {
        return typeof data === "string" ? data : JSON.stringify(data, null, 2);
      } catch (e) {
        return String(data);
      }
    })()}
  </pre>
);

const SectionTitle = ({ title }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: C.coral,
      marginTop: 18,
      marginBottom: 4,
    }}
  >
    {title}
  </div>
);

// The transaction reference, read the same way the server reads it: the Order API
// exposes it as payments[].reference while the hosted page uses transaction.ref.
// This is the value Telr support asks for, and the one a refund is issued against.
const telrTransactionRef = (raw) => {
  if (!raw) return null;
  const list = Array.isArray(raw.payments) ? raw.payments : [];
  const last = list.length ? list[list.length - 1] : null;
  const t = last || raw.transaction || null;
  if (!t) return null;
  return t.ref ?? t.transactionRef ?? t.reference ?? t.id ?? null;
};

// Whether Telr will take Apple Pay on this order, read from the CREATION response — the
// only Telr payload that carries allowedPaymentMethods / _links.applePay. A "No" here
// is why an Apple Pay attempt failed inside the SDK with nothing else to go on, and it is
// a store-configuration matter for Telr support, not an app bug.
const applePayAllowedBy = (created) => {
  if (!created) return null;
  const methods = created.allowedPaymentMethods;
  if (Array.isArray(methods)) {
    return methods.includes("APPLE_PAY")
      ? "Yes"
      : `No — Telr allows only: ${methods.join(", ") || "(none)"}`;
  }
  if (created._links?.applePay?.href) return "Yes";
  return "Unknown — Telr's response did not list payment methods";
};

const SessionDetailModal = ({ session, onClose, customerName }) => {
  if (!session) return null;
  const raw = session.rawResponse || null;
  const created = session.createResponse || null;
  return (
    <Modal isOpen={!!session} toggle={onClose} centered size="lg">
      <style>{reportingGlobalCSS}</style>
      <ModalBody style={{ padding: 0 }} className="rpt-wrap">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Attempt #{session.id}</span>
            <Pill value={session.status} palette={STATUS_COLOR} />
          </div>
          <div onClick={onClose} style={{ cursor: "pointer", color: C.textMed, display: "flex" }} title="Close">
            <X size={20} />
          </div>
        </div>

        <div style={{ padding: "12px 22px 22px" }}>
          <SectionTitle title="Attempt" />
          <DetailRow label="Status" value={`${session.status} — ${STATUS_HELP[session.status] || ""}`} />
          <DetailRow label="Amount" value={`${session.amount ?? "—"} ${session.currency ?? ""}`} />
          <DetailRow label="Method" value={METHOD_LABEL[session.method] || session.method} />
          <DetailRow label="Card" value={cardLabel(session)} />
          <DetailRow label="Started" value={formatDate(session.createdAt)} />
          <DetailRow label="Confirmed" value={session.verifiedAt ? formatDate(session.verifiedAt) : null} />

          <SectionTitle title="References" />
          <DetailRow label="Telr order ref" value={session.ref} mono />
          <DetailRow label="Transaction ref" value={telrTransactionRef(raw)} mono />
          <DetailRow label="Cart id sent to Telr" value={session.cartId} mono />
          <DetailRow label="Our external id" value={session.externalId} mono />
          {session.method === "applepay" && (
            <DetailRow label="Apple Pay allowed by Telr" value={applePayAllowedBy(created)} />
          )}

          <SectionTitle title="Who" />
          <DetailRow label="Customer" value={`${customerName(session.User)}${session.UserId ? ` (#${session.UserId})` : ""}`} />
          <DetailRow label="Merchant" value={session.Merchant?.name} />
          <DetailRow label="Branch" value={session.Branch?.name} />

          <SectionTitle title="Order" />
          {session.order ? (
            <Fragment>
              <DetailRow label="Order" value={`#${session.order.id} (${session.order.status}, ${session.order.payment})`} />
              {session.status !== "paid" && (
                <DetailRow
                  label="Note"
                  value="This attempt did not succeed. The order above belongs to the same cart and was paid by a different attempt."
                />
              )}
            </Fragment>
          ) : (
            <DetailRow
              label="Order"
              value={
                session.status === "paid"
                  ? "No order yet — money was captured. The reconcile sweep should create it within 5 minutes; if it has not, this needs attention."
                  : "None, as expected for an attempt that never completed."
              }
            />
          )}

          {raw && (
            <Fragment>
              <SectionTitle title="Last response from Telr" />
              <JsonBlock data={raw} />
            </Fragment>
          )}

          {created && (
            <Fragment>
              <SectionTitle title="Response when created" />
              <JsonBlock data={created} />
            </Fragment>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    date.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true })
  );
};

export default TelrSessions;
